import { apiClient } from "@/shared/api/client"
import type { WaterQualityResponse, PaginatedResponse, PaginationParams } from "@/shared/types/api"

export interface WaterQualityListParams extends PaginationParams {
  pondId?: string | null
  startDate?: string
  endDate?: string
  sortOrder?: 'asc' | 'desc'
  filter?: 'has_issues' | 'microcystis'
}

export interface WaterQualityCreateRequest {
  dissolvedOxygen: number
  ph: number
  temperature: number
  testedAt: string
  salinity?: number
  ammonia?: number
  nitrite?: number
  nitrate?: number
  turbidity?: number
  notes?: string
  imageIds?: string[]
}

export type WaterQualityUpdateRequest = Partial<WaterQualityCreateRequest>

export const waterQualityApi = {
  // Get a single water quality test by ID
  get: async (
    farmId: string,
    pondId: string,
    testId: string
  ): Promise<WaterQualityResponse> => {
    const response = await apiClient.get<{ data: WaterQualityResponse }>(
      `/farms/${farmId}/ponds/${pondId}/water-quality/${testId}`
    )
    return response.data.data
  },

  // Create a new water quality test
  create: async (
    farmId: string,
    pondId: string,
    data: WaterQualityCreateRequest
  ): Promise<WaterQualityResponse> => {
    const response = await apiClient.post<{ data: WaterQualityResponse }>(
      `/farms/${farmId}/ponds/${pondId}/water-quality`,
      data
    )
    return response.data.data
  },

  // Update an existing water quality test
  update: async (
    farmId: string,
    pondId: string,
    testId: string,
    data: WaterQualityUpdateRequest
  ): Promise<WaterQualityResponse> => {
    const response = await apiClient.put<{ data: WaterQualityResponse }>(
      `/farms/${farmId}/ponds/${pondId}/water-quality/${testId}`,
      data
    )
    return response.data.data
  },

  // Legacy pond-specific endpoint (kept for backward compatibility)
  list: async (
    farmId: string,
    pondId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<WaterQualityResponse>> => {
    const response = await apiClient.get<PaginatedResponse<WaterQualityResponse>>(
      `/farms/${farmId}/ponds/${pondId}/water-quality`,
      { params }
    )
    return response.data
  },

  // New farm-wide endpoint with optional pond filtering
  listByFarm: async (
    farmId: string,
    params?: WaterQualityListParams
  ): Promise<PaginatedResponse<WaterQualityResponse>> => {
    const { pondId, ...restParams } = params || {}

    // If pondId is provided, use pond-specific endpoint
    if (pondId) {
      const response = await apiClient.get<PaginatedResponse<WaterQualityResponse>>(
        `/farms/${farmId}/ponds/${pondId}/water-quality`,
        { params: restParams }
      )
      return response.data
    }

    // Backend does not support farm-wide water-quality list endpoint yet
    // Return empty results when no pond is selected
    // Users must select a pond to view water quality records
    return {
      data: [],
      keysetPagination: {
        hasNext: false,
        limit: params?.limit || 20,
      },
    }
  },
}
